import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement } from "react";
import {
  Field,
  FormPage,
  FormSection,
  MoneyInput,
  QtyInput,
  QueryStatus,
  ScaledInput,
  useToast,
} from "@cx/ui";
import type { ContactListItem, ListResult, QuoteCalc, QuoteFormOptions, QuoteView } from "@cx/contracts";
import {
  COMMERCIAL_TERMS_LABELS,
  COMMERCIAL_TERMS_MODES,
  FREIGHT_MODES,
  FREIGHT_MODE_LABELS,
  formatBRL,
  formatDateBR,
  formatPct,
  todayInSaoPaulo,
  toPct4,
  type CommercialTermsMode,
  type FreightMode,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { TaxEstimatePanel } from "../fiscal/TaxEstimate";
import { ProductSearch } from "../prices/ProductSearch";
import {
  emptyQuoteForm,
  formFromView,
  itemError,
  previewBody,
  quoteBody,
  rowKey,
  type ItemRow,
  type QuoteForm,
} from "./labels";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/** Inclusão (/vendas/propostas/novo) e edição (/vendas/propostas/$id/editar) da proposta. */
export function QuoteFormPage(): ReactElement {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const { companyId, api } = useSession();
  const options = useQuery({
    queryKey: ["quotes", companyId, "form-options"],
    queryFn: () => api<QuoteFormOptions>("/quotes/form-options"),
  });
  const quote = useQuery({
    queryKey: ["quotes", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<QuoteView>(`/quotes/${id}`),
  });
  if (!options.data || (id && !quote.data))
    return (
      <QueryStatus loading={options.isLoading || quote.isLoading} error={options.error ?? quote.error} />
    );
  if (quote.data?.convertedOrderId)
    return (
      <div className="alert alert-warning" role="alert">
        Proposta convertida em pedido: não pode mais ser alterada.
      </div>
    );
  return <QuoteFormBody key={id ?? "new"} options={options.data} quote={quote.data ?? null} />;
}

function QuoteFormBody({ options, quote }: { options: QuoteFormOptions; quote: QuoteView | null }) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // Usuário-vendedor só tem a si mesmo nas opções: já vem escolhido.
  const onlySeller = options.sellerLocked ? (options.sellers[0]?.id ?? "") : "";
  const [f, setF] = useState<QuoteForm>(() =>
    quote ? formFromView(quote) : emptyQuoteForm(todayInSaoPaulo(), onlySeller),
  );
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por tela de inclusão: clique duplo ou repetição da rede não criam duas propostas.
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = <K extends keyof QuoteForm>(k: K, v: QuoteForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setItem = (key: string, patch: Partial<ItemRow>) => {
    setF((x) => ({ ...x, items: x.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
    setDirty(true);
  };

  const pb = previewBody(f);
  const preview = useQuery({
    queryKey: ["quotes", companyId, "preview", pb],
    enabled: !!pb,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () => api<QuoteCalc>("/quotes/preview", { method: "POST", body: JSON.stringify(pb) }),
  });
  const calc = pb ? preview.data : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(quoteBody(f));
      if (quote) {
        await api(`/quotes/${quote.id}`, { method: "PUT", body });
        return quote.id;
      }
      const r = await api<{ id: string }>("/quotes", {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idemKey },
      });
      return r.id;
    },
    onSuccess: (savedId) => {
      toast("success", "Proposta salva");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["quotes", companyId] });
      navigate({ to: "/vendas/propostas/$id", params: { id: savedId } });
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      if (e instanceof ApiFailure && e.code === "invalid_contact") setErrors({ contactId: e.message });
      toast("danger", errorMessage(e));
    },
  });

  const title = quote ? `Proposta nº ${quote.number}` : "Nova proposta comercial";
  const back = () =>
    quote
      ? navigate({ to: "/vendas/propostas/$id", params: { id: quote.id } })
      : navigate({ to: "/vendas/propostas" });
  const previewError = pb && preview.error ? errorMessage(preview.error) : null;

  return (
    <FormPage
      title={title}
      breadcrumb={["Vendas", "Propostas comerciais", quote ? String(quote.number) : "Nova"]}
      onSave={() => save.mutate()}
      onCancel={back}
      saving={save.isPending}
      dirty={dirty}
      aside={
        // Totais e impostos estimados ficam juntos à vista ao rolar os itens.
        <div className="position-sticky" style={{ top: "1rem" }}>
          <TotalsBox calc={calc} error={previewError} loading={preview.isFetching} />
          <TaxEstimatePanel
            contactId={f.contact?.id ?? null}
            lines={(calc?.items ?? []).map((i) => ({
              productId: i.productId,
              qtyQ4: i.qtyQ4,
              unitPriceCents: i.listPriceCents,
              totalCents: i.totalCents,
            }))}
            discountCents={calc?.discountCents ?? 0}
            freightCents={calc?.freightCents ?? 0}
            otherExpensesCents={calc?.otherExpensesCents ?? 0}
          />
        </div>
      }
    >
      <FormSection title="Cliente e proposta">
        <div className="row">
          <div className="col-md-8 mb-3">
            <ContactPicker value={f.contact} error={errors.contactId} onChange={(c) => set("contact", c)} />
          </div>
          <Field label="A/C (aos cuidados de)" className="col-md-4" error={errors.attentionOf}>
            <input
              value={f.attentionOf}
              maxLength={120}
              onChange={(e) => set("attentionOf", e.target.value)}
            />
          </Field>
          <Field label="Data" required className="col-md-3" error={errors.date}>
            <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
          </Field>
          <Field label="Válida até" className="col-md-3" error={errors.validUntil}>
            <input type="date" value={f.validUntil} onChange={(e) => set("validUntil", e.target.value)} />
          </Field>
          <Field label="Próximo contato" className="col-md-3" error={errors.nextContactAt}>
            <input
              type="date"
              value={f.nextContactAt}
              onChange={(e) => set("nextContactAt", e.target.value)}
            />
          </Field>
          <Field label="Vendedor" className="col-md-3" error={errors.sellerId}>
            <select
              className="form-select"
              value={f.sellerId}
              disabled={options.sellerLocked}
              onChange={(e) => set("sellerId", e.target.value)}
            >
              {!options.sellerLocked && <option value="">Sem vendedor</option>}
              {options.sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Lista de preços"
            className="col-md-6"
            error={errors.priceListId}
            hint="Ao trocar a lista, os preços dos produtos são recalculados."
          >
            <select
              className="form-select"
              value={f.priceListId}
              onChange={(e) => {
                const v = e.target.value;
                setF((x) => ({
                  ...x,
                  priceListId: v,
                  items: x.items.map((i) => (i.productId ? { ...i, listPriceCents: null } : i)),
                }));
                setDirty(true);
              }}
            >
              <option value="">Preço de venda do produto</option>
              {/* Lista gravada que não vem nas opções (base "custo" para quem não vê custo): mantém o nome na tela. */}
              {quote?.priceList &&
                quote.priceList.id === f.priceListId &&
                !options.priceLists.some((l) => l.id === quote.priceList!.id) && (
                  <option value={quote.priceList.id}>{quote.priceList.name}</option>
                )}
              {options.priceLists
                .filter((l) => l.active || l.id === f.priceListId)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Introdução" className="col-12" error={errors.introduction}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.introduction}
              onChange={(e) => set("introduction", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Itens">
        {errors.items && <div className="alert alert-danger py-2">{errors.items}</div>}
        <ItemsTable
          f={f}
          calc={calc}
          errors={errors}
          setItem={setItem}
          onRemove={(key) =>
            set(
              "items",
              f.items.filter((i) => i.key !== key),
            )
          }
        />
        <div className="row align-items-end">
          <div className="col-md-8">
            <ProductSearch
              id="quote-product"
              label="Adicionar produto"
              action="Adicionar"
              onPick={(p) =>
                set("items", [
                  ...f.items,
                  {
                    key: rowKey(),
                    productId: p.id,
                    description: p.name,
                    code: p.sku,
                    unit: p.unit ?? "",
                    qtyQ4: 10_000,
                    listPriceCents: null,
                    discountPct4: null,
                  },
                ])
              }
            />
          </div>
          {options.canAddOtherItems && (
            <div className="col-md-4 mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() =>
                  set("items", [
                    ...f.items,
                    {
                      key: rowKey(),
                      productId: null,
                      description: "",
                      code: null,
                      unit: "",
                      qtyQ4: 10_000,
                      listPriceCents: null,
                      discountPct4: null,
                    },
                  ])
                }
              >
                Adicionar outro item
              </button>
            </div>
          )}
        </div>
      </FormSection>

      <FormSection title="Descontos, frete e despesas">
        <div className="row">
          <fieldset className="col-md-6 mb-3">
            <legend className="form-label fs-6">Desconto geral</legend>
            {(
              [
                ["value", "Em valor"],
                ["pct", "Em percentual"],
              ] as const
            ).map(([m, label]) => (
              <div key={m} className="form-check form-check-inline">
                <input
                  id={`quote-discount-${m}`}
                  type="radio"
                  name="quote-discount-mode"
                  className="form-check-input"
                  checked={f.discountMode === m}
                  onChange={() => set("discountMode", m)}
                />
                <label className="form-check-label" htmlFor={`quote-discount-${m}`}>
                  {label}
                </label>
              </div>
            ))}
          </fieldset>
          {f.discountMode === "value" ? (
            <Field label="Desconto" className="col-md-6" error={errors.discountCents}>
              <MoneyInput value={f.discountCents} onChange={(v) => set("discountCents", v)} />
            </Field>
          ) : (
            <Field label="Desconto (%)" className="col-md-6" error={errors.discountPct4}>
              <ScaledInput
                value={f.discountPct4}
                parse={toPct4}
                format={pctNumber}
                suffix="%"
                onChange={(v) => set("discountPct4", v)}
              />
            </Field>
          )}
          <Field label="Frete" className="col-md-6" error={errors.freightCents}>
            <MoneyInput value={f.freightCents} onChange={(v) => set("freightCents", v)} />
          </Field>
          <Field label="Outras despesas" className="col-md-6" error={errors.otherExpensesCents}>
            <MoneyInput value={f.otherExpensesCents} onChange={(v) => set("otherExpensesCents", v)} />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Condições comerciais">
        <div className="row">
          <Field label="Condição comercial" className="col-md-4">
            <select
              className="form-select"
              value={f.commercialTermsMode}
              onChange={(e) => set("commercialTermsMode", e.target.value as CommercialTermsMode)}
            >
              {COMMERCIAL_TERMS_MODES.map((m) => (
                <option key={m} value={m}>
                  {COMMERCIAL_TERMS_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
          {f.commercialTermsMode === "installments" && (
            <Field label="Condição de pagamento" required className="col-md-8" error={errors.paymentTermId}>
              <select
                className="form-select"
                value={f.paymentTermId}
                onChange={(e) => set("paymentTermId", e.target.value)}
              >
                <option value="">Escolha…</option>
                {options.paymentTerms
                  .filter((t) => t.active || t.id === f.paymentTermId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          {f.commercialTermsMode === "text" && (
            <Field label="Texto da condição" required className="col-md-8" error={errors.commercialTermsText}>
              <textarea
                rows={2}
                maxLength={2000}
                value={f.commercialTermsText}
                onChange={(e) => set("commercialTermsText", e.target.value)}
              />
            </Field>
          )}
        </div>
        {f.commercialTermsMode === "installments" && calc && calc.installments.length > 0 && (
          <table className="table table-sm w-auto" aria-label="Parcelas">
            <thead>
              <tr>
                <th scope="col">Parcela</th>
                <th scope="col">Vencimento</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {calc.installments.map((p) => (
                <tr key={p.number}>
                  <td>{p.number}</td>
                  <td>{formatDateBR(p.dueDate)}</td>
                  <td className="text-end">{formatBRL(p.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FormSection>

      <FormSection
        title="Transporte e condições gerais"
        defaultOpen={!!(quote?.carrier || quote?.generalTerms)}
      >
        <div className="row">
          <Field label="Transportador" className="col-md-6" error={errors.carrierId}>
            <select
              className="form-select"
              value={f.carrierId}
              onChange={(e) => set("carrierId", e.target.value)}
            >
              <option value="">Nenhum</option>
              {options.carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Modalidade do frete" className="col-md-6">
            <select
              className="form-select"
              value={f.freightMode}
              onChange={(e) => set("freightMode", Number(e.target.value) as FreightMode)}
            >
              {FREIGHT_MODES.map((m) => (
                <option key={m} value={m}>
                  {FREIGHT_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Condições gerais" className="col-12" error={errors.generalTerms}>
            <textarea
              rows={3}
              maxLength={5000}
              value={f.generalTerms}
              onChange={(e) => set("generalTerms", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>
    </FormPage>
  );
}

function ItemsTable({
  f,
  calc,
  errors,
  setItem,
  onRemove,
}: {
  f: QuoteForm;
  calc: QuoteCalc | undefined;
  errors: Record<string, string>;
  setItem(key: string, patch: Partial<ItemRow>): void;
  onRemove(key: string): void;
}) {
  if (f.items.length === 0) return <p className="text-muted">Nenhum item. Busque um produto abaixo.</p>;
  return (
    <div className="table-responsive mb-3">
      <table className="table align-middle" aria-label="Itens da proposta">
        <thead>
          <tr>
            <th scope="col">Descrição</th>
            <th scope="col" style={{ width: "5rem" }}>
              Un
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              Qtd
            </th>
            <th scope="col" style={{ width: "10rem" }} className="text-end">
              Preço un.
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              Desc. %
            </th>
            <th scope="col" className="text-end">
              Total
            </th>
            <th scope="col">
              <span className="visually-hidden">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {f.items.map((it, i) => {
            const line = calc?.items[i];
            const err = itemError(errors, i);
            const name = it.description || `item ${i + 1}`;
            return (
              <tr key={it.key}>
                <td>
                  {it.productId ? (
                    <>
                      {it.description}
                      {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                    </>
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      aria-label={`Descrição do item ${i + 1}`}
                      maxLength={1000}
                      value={it.description}
                      onChange={(e) => setItem(it.key, { description: e.target.value })}
                    />
                  )}
                  {err && <div className="invalid-feedback d-block">{err}</div>}
                </td>
                <td>
                  {it.productId ? (
                    it.unit || "—"
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      aria-label={`Unidade do item ${i + 1}`}
                      maxLength={6}
                      value={it.unit}
                      onChange={(e) => setItem(it.key, { unit: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  <QtyInput
                    aria-label={`Quantidade de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.qtyQ4}
                    onChange={(v) => setItem(it.key, { qtyQ4: v })}
                  />
                </td>
                <td>
                  <MoneyInput
                    aria-label={`Preço unitário de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.listPriceCents ?? line?.listPriceCents ?? null}
                    onChange={(v) => setItem(it.key, { listPriceCents: v })}
                  />
                </td>
                <td>
                  <ScaledInput
                    aria-label={`Desconto de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.discountPct4}
                    parse={toPct4}
                    format={pctNumber}
                    onChange={(v) => setItem(it.key, { discountPct4: v })}
                  />
                </td>
                <td className="text-end text-nowrap">{line ? formatBRL(line.totalCents) : "—"}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remover ${name}`}
                    onClick={() => onRemove(it.key)}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBox({
  calc,
  error,
  loading,
}: {
  calc: QuoteCalc | undefined;
  error: string | null;
  loading: boolean;
}) {
  const rows: [string, number][] = calc
    ? [
        ["Total dos itens", calc.itemsTotalCents],
        ["Outros itens", calc.otherItemsTotalCents],
        ["Desconto", -calc.discountCents],
        ["Frete", calc.freightCents],
        ["Outras despesas", calc.otherExpensesCents],
      ]
    : [];
  return (
    <div className="box card p-3 mb-3" aria-live="polite">
      <h2 className="h6">Totais</h2>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {!calc && !error && <p className="text-muted small mb-0">Escolha o cliente e inclua itens.</p>}
      {calc && (
        <dl className="row mb-0 small" aria-busy={loading}>
          {rows.map(([label, v]) => (
            <Fragment key={label}>
              <dt className="col-7 fw-normal">{label}</dt>
              <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
            </Fragment>
          ))}
          <dt className="col-7">Total da proposta</dt>
          <dd className="col-5 text-end fw-bold mb-0" data-testid="quote-total">
            {formatBRL(calc.totalCents)}
          </dd>
        </dl>
      )}
    </div>
  );
}

/** Cliente da proposta: busca por nome ou CPF/CNPJ (usuário-vendedor só acha a própria carteira). */
function ContactPicker({
  value,
  error,
  onChange,
}: {
  value: { id: string; name: string } | null;
  error?: string;
  onChange(c: { id: string; name: string } | null): void;
}) {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["contacts", companyId, { q: term, picker: "quote" }],
    enabled: !value && term.length >= 2,
    queryFn: () =>
      api<ListResult<ContactListItem>>(`/contacts?${new URLSearchParams({ q: term, limit: "8" })}`),
  });
  if (value)
    return (
      <div>
        <span className="form-label d-block">
          Cliente{" "}
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        </span>
        <p className="mb-0" data-testid="quote-contact">
          <strong>{value.name}</strong>{" "}
          <button type="button" className="btn btn-sm btn-link" onClick={() => onChange(null)}>
            Trocar
          </button>
        </p>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    );
  return (
    <div>
      <label className="form-label" htmlFor="quote-contact-search">
        Cliente{" "}
        <span className="text-danger" aria-hidden="true">
          *
        </span>
      </label>
      <input
        id="quote-contact-search"
        type="search"
        className={`form-control${error ? " is-invalid" : ""}`}
        placeholder="Nome ou CPF/CNPJ"
        aria-invalid={error ? "true" : undefined}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Clientes encontrados">
          {found.isSuccess && found.data.data.length === 0 && (
            <li className="list-group-item text-muted">Nenhum contato encontrado.</li>
          )}
          {found.data?.data.map((c) => (
            <li key={c.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">{c.name}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Escolher ${c.name}`}
                onClick={() => {
                  onChange({ id: c.id, name: c.name });
                  setQ("");
                }}
              >
                Escolher
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
